// SPDX-License-Identifier: AGPL-3.0-or-later

import type { ApiError } from '../api/client.js';

export interface IntakeLinkErrorProps {
  error: unknown;
}

/**
 * Patient-facing failure screen for the magic-link bootstrap step. The
 * API returns `{ code: 'expired' | 'invalid_token' | ... }`. We map each
 * code to a human screen with copy that names a real next step — the
 * patient should never see a stack trace or "Request failed: 410".
 */
export function IntakeLinkError({ error }: IntakeLinkErrorProps) {
  const variant = classify(error);

  return (
    <main className="app-main intake-link-error" id="main-content" tabIndex={-1}>
      <div className={`intake-link-error-card intake-link-error-${variant.kind}`}>
        <div className="intake-link-error-icon" aria-hidden="true">
          {variant.icon}
        </div>
        <h1 className="intake-link-error-title">{variant.title}</h1>
        <p className="intake-link-error-body">{variant.body}</p>
        <p className="intake-link-error-action muted">{variant.nextStep}</p>
      </div>
    </main>
  );
}

interface Variant {
  kind: 'expired' | 'consumed' | 'invalid' | 'network';
  icon: string;
  title: string;
  body: string;
  nextStep: string;
}

function classify(error: unknown): Variant {
  const apiError = isApiError(error) ? error : undefined;

  // 410 + code 'expired' — link past its TTL.
  if (apiError?.status === 410 || apiError?.code === 'expired') {
    return {
      kind: 'expired',
      icon: '⌛',
      title: 'This link has expired',
      body: 'For your security, intake links expire after a short time.',
      nextStep:
        'Ask your clinic for a new link. They can reissue it from the dashboard in a few seconds.',
    };
  }

  // 401 + code 'invalid_token' covers "already consumed" (one-shot
  // submit), "token hash mismatch", and "not found". Treat the consumed
  // case as the most common — every other invalid_token is rare in the
  // wild.
  if (apiError?.status === 401 || apiError?.code === 'invalid_token') {
    return {
      kind: 'consumed',
      icon: '🔒',
      title: 'This link can no longer be used',
      body: 'This intake may already have been completed, or the link was deactivated by your clinic.',
      nextStep: 'If you still need to fill out the form, ask your clinic for a new link.',
    };
  }

  // No status (offline, DNS failure, CORS) — network class.
  if (apiError === undefined) {
    return {
      kind: 'network',
      icon: '📡',
      title: 'We couldn’t reach the server',
      body: 'Your internet connection may be offline.',
      nextStep: 'Check your connection and refresh the page to try again.',
    };
  }

  return {
    kind: 'invalid',
    icon: '⚠️',
    title: 'We couldn’t open this form',
    body: apiError.message,
    nextStep: 'Refresh the page, or ask your clinic for a new link.',
  };
}

function isApiError(err: unknown): err is ApiError {
  return (
    err !== null &&
    typeof err === 'object' &&
    'status' in err &&
    'code' in err &&
    typeof (err as { status: unknown }).status === 'number'
  );
}
